import * as React from "react";
import { omitBy } from "lodash-es";
import { casesActions } from "configs";
import {
  FormWatcherCallback,
  PartialNonNullable,
  resolveNumber,
  resolveString,
  useCurrentCase,
  useDispatch,
  useParams
} from "duck";
import { useUpdateGeneralAssessmentMutation } from "requests";
import { GeneralAssessmentValues, StringFieldsToResolve } from "./types";

const stringFieldsToResolve: StringFieldsToResolve = [
  "between25And50PerCentBoneLoss",
  "externalCervicalToothResorption",
  "externalInflammatoryToothResorption",
  "externalReplacementRootResorption",
  "externalSurfaceRootResorption",
  "greaterThan50PerCentBoneLoss",
  "internalToothResorption",
  "lessThan25PerCentBoneLoss",
  "missingTeeth",
  "periapicalLucency",
  "periapicalRarefaction",
  "retainedToothRoots",
  "toothResorptionType1",
  "toothResorptionType2",
  "toothResorptionType3",
  "uneruptedTeeth",
  "widenedPulpCanal"
];

const resolveStringFields = (
  values: PartialNonNullable<GeneralAssessmentValues>
) => {
  return stringFieldsToResolve.reduce((acc, field) => {
    acc[field] = values[field] === "" ? [] : values[field]?.split(", ");
    return acc;
  }, {} as Record<keyof GeneralAssessmentValues, string[] | undefined>);
};

export const useUpdateGeneralAssessment = () => {
  const dispatch = useDispatch();
  const { id: caseId } = useParams();
  const [update] = useUpdateGeneralAssessmentMutation({ ignoreResults: true });
  const { generalAssessmentId, patientId, shouldSync } = useCurrentCase(
    medCase => ({
      generalAssessmentId:
        medCase.data.client.patient.generalAssessment?._id || null,
      patientId: medCase.data.client.patient._id,
      shouldSync: medCase.shouldSync
    })
  );

  return React.useCallback<
    FormWatcherCallback<Partial<GeneralAssessmentValues>>
  >(
    values => {
      const resolvedStringData = resolveStringFields(values);
      const data = omitBy(
        {
          ...values,
          ...resolvedStringData,
          rightInfraorbital: resolveNumber(
            values.rightInfraorbital,
            true,
            true
          ),
          rightMaxillary: resolveNumber(values.rightMaxillary, true, true),
          rightRostralMandibular: resolveNumber(
            values.rightRostralMandibular,
            true,
            true
          ),
          rightInferiorAlveolar: resolveNumber(
            values.rightInferiorAlveolar,
            true,
            true
          ),
          leftInfraorbital: resolveNumber(values.leftInfraorbital, true, true),
          leftMaxillary: resolveNumber(values.leftMaxillary, true, true),
          leftRostralMandibular: resolveNumber(
            values.leftRostralMandibular,
            true,
            true
          ),
          leftInferiorAlveolar: resolveNumber(
            values.leftInferiorAlveolar,
            true,
            true
          ),
          boneAugmentationDetails: resolveString(values.boneAugmentationDetails)
        },
        value => value === undefined
      );

      dispatch(casesActions.updateGeneralAssessment({ _id: caseId, data }));

      if (shouldSync && generalAssessmentId) {
        return update({
          variables: {
            input: {
              _id: patientId,
              generalAssessment: { _id: generalAssessmentId, ...data }
            }
          }
        }).then(response => {
          const shouldUpdateDentalData = data.dentalChart !== undefined;
          if (!shouldUpdateDentalData) {
            return;
          }
          const dentalChartings =
            response.data?.updatePatient?.patient?.dentalChartings?.collection.map(
              chart => ({ ...chart, tooth: chart.tooth._id })
            ) || null;
          const dentalComments =
            response.data?.updatePatient?.patient?.dentalSketchComments
              ?.collection || [];
          const dentalSketch =
            response.data?.updatePatient?.patient?.dentalSketch?._id || null;

          dispatch(
            casesActions.setDentalChartings({
              data: dentalChartings,
              _id: caseId
            })
          );

          dispatch(
            casesActions.setDentalSketchComments({
              data: dentalComments,
              _id: caseId
            })
          );

          dispatch(
            casesActions.updateDentalSketch({
              data: { dentalSketch },
              _id: caseId
            })
          );
        });
      }
    },
    [generalAssessmentId, update, shouldSync]
  );
};
